export type WamSuggestionMode = 'single' | 'unlimited';
export type WamSuggestionStatus = 'pending' | 'used' | 'rejected';
export type WamConnectionStatus = 'online' | 'starting' | 'recovering' | 'degraded' | 'offline' | 'disabled';

export type WamSuggestion = {
  id: string;
  number: number;
  user: string;
  text: string;
  status: WamSuggestionStatus;
  voteCount: number;
  submittedAt: string | null;
  updatedAt: string | null;
  usedAt: string | null;
  rejectedAt: string | null;
};

export type WamSelection = {
  id: string;
  category: string;
  source: string;
  timestamp: string;
  visual?: 'cah' | null;
  prompt?: string | null;
  suggestedBy?: string | null;
};

export type WamSnapshot = {
  agentId: string | null;
  agentName: string | null;
  agentConnected: boolean;
  suggestionChatAcknowledgementsEnabled: boolean;
  service: {
    status: WamConnectionStatus;
    baseUrl: string | null;
    current: WamSelection | null;
    twitchStatus: string;
    nativeCommandMode: boolean;
    suggestionsOpen: boolean;
    suggestionMode: WamSuggestionMode;
    votingOpen: boolean;
    suggestions: WamSuggestion[];
    categoryCount: number;
    weekendRemaining: number | null;
    recentHistory: WamSelection[];
    managed: boolean;
    processState: 'not-managed' | 'external' | 'owned' | 'stopped' | 'unknown';
    restartAttempts: number;
    consecutiveHealthFailures: number;
    lastHealthyAt: string | null;
    nextRetryAt: string | null;
    terminalFailure: boolean;
    lastError: string | null;
    updatedAt: string;
  };
};

export type WamResultPlayer = {
  sourceName?: string;
  source_name?: string;
  twitchUserId?: string | null;
  twitch_user_id?: string | null;
  score: number | null;
  placement: number | null;
  winner: boolean;
};

export type WamResult = {
  id: string;
  source_result_id: string;
  game_session_id: string | null;
  result_state: 'partial' | 'complete' | 'failed';
  room_code: string | null;
  category: string | null;
  stream_session_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  observed_at: string;
  occurred_at: string;
  player_count: number;
  players: WamResultPlayer[];
};

export type WamResultsStatus = {
  source: {
    liveIngestionEnabled: boolean;
    supportedResultFeed: boolean;
    transportObserved: string;
    reason: string;
  };
  current: {
    roomUrl: string | null;
    roomCode: string | null;
    streamSessionId: string | null;
    resultDataAvailable: boolean;
  };
  latestResult: WamResult | null;
};

export type WamResultDetail = {
  result: WamResult;
  players: Array<{
    id: string;
    source_name: string;
    twitch_user_id: string | null;
    score: number | null;
    placement: number | null;
    winner: boolean;
  }>;
  rounds: Array<{
    id: string;
    round_number: number;
    chosen_by_name: string | null;
    song_title: string | null;
    artist_name: string | null;
  }>;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(body.message ?? body.error ?? `Request failed with HTTP ${response.status}.`);
  }
  return (await response.json().catch(() => ({}))) as T;
}

export function loadWam(): Promise<WamSnapshot> {
  return request('/api/v1/wam');
}

export function runWamAction(action: string, payload: Record<string, unknown> = {}): Promise<{ ok: true; snapshot: WamSnapshot }> {
  return request('/api/v1/wam/actions', {
    method: 'POST',
    body: JSON.stringify({ action, payload }),
  });
}

export async function loadWamResults(search = '', state: 'all' | WamResult['result_state'] = 'all') {
  const params = new URLSearchParams({ limit: '50', offset: '0' });
  if (search.trim()) params.set('search', search.trim());
  if (state !== 'all') params.set('state', state);
  const [status, list] = await Promise.all([
    request<WamResultsStatus>('/api/v1/wam/results/status'),
    request<{ results: WamResult[]; total: number; hasMore: boolean }>('/api/v1/wam/results?' + params.toString()),
  ]);
  return { status, ...list };
}

export function loadWamResultDetail(id: string): Promise<WamResultDetail> {
  return request(`/api/v1/wam/results/${encodeURIComponent(id)}`);
}
